/**
 * Combined version history for the 3D Model step — 3D HTML model + 2D concept elevations
 * in one dialog, triggered from the step card header.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, X, ExternalLink, ChevronDown, ChevronUp, Box, Layers2, Video } from 'lucide-react';
import { loomUrlForRelease } from '../../utils/portalModelLoom';
import type { PortalDeliverableApproval, PortalFileRelease, PortalReleaseBundle } from '../../types';
import { portalHostedAssetHref } from '../../utils/portalAssetUrl';
import { formatPortalReleaseAddedAt } from '../../utils/portalReleaseDateFormat';
import {
  collectDeliverableApprovals,
  findApprovalForBundle,
  formatApprovalBadge,
} from '../../utils/portalDeliverableApprovalService';
import './PortalModelStepVersionsPopover.css';

export interface PortalModelStepVersionsPopoverProps {
  modelBundles: PortalReleaseBundle[];
  modelReleases: PortalFileRelease[];
  conceptBundles: PortalReleaseBundle[];
  conceptReleases: PortalFileRelease[];
  modelApproval?: PortalDeliverableApproval;
  modelApprovalHistory?: PortalDeliverableApproval[];
  conceptApproval?: PortalDeliverableApproval;
  conceptApprovalHistory?: PortalDeliverableApproval[];
  onOpenRelease?: (release: PortalFileRelease, bundle?: PortalReleaseBundle) => void;
  onOpenWalkthrough?: (release: PortalFileRelease, bundle?: PortalReleaseBundle) => void;
  /** Compact pill for the step card header badge row. */
  variant?: 'inline' | 'header';
}

function useReleaseById(releases: PortalFileRelease[]) {
  return useMemo(() => {
    const map = new Map<string, PortalFileRelease>();
    for (const r of releases) {
      if (r.id) map.set(r.id, r);
    }
    return map;
  }, [releases]);
}

function sortBundles(bundles: PortalReleaseBundle[]) {
  return [...bundles].sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex);
}

const PortalModelStepVersionsPopover: React.FC<PortalModelStepVersionsPopoverProps> = ({
  modelBundles,
  modelReleases,
  conceptBundles,
  conceptReleases,
  modelApproval,
  modelApprovalHistory,
  conceptApproval,
  conceptApprovalHistory,
  onOpenRelease,
  onOpenWalkthrough,
  variant = 'inline',
}) => {
  const [open, setOpen] = useState(false);
  /** Multi-file bundles start expanded; track which the user collapsed. */
  const [collapsedBundleKeys, setCollapsedBundleKeys] = useState<Set<string>>(() => new Set());
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const firstOpenLinkRef = useRef<HTMLAnchorElement | null>(null);

  const modelReleaseById = useReleaseById(modelReleases);
  const conceptReleaseById = useReleaseById(conceptReleases);
  const sortedModel = useMemo(() => sortBundles(modelBundles), [modelBundles]);
  const sortedConcept = useMemo(() => sortBundles(conceptBundles), [conceptBundles]);
  const modelApprovals = useMemo(
    () => collectDeliverableApprovals(modelApproval, modelApprovalHistory),
    [modelApproval, modelApprovalHistory],
  );
  const conceptApprovals = useMemo(
    () => collectDeliverableApprovals(conceptApproval, conceptApprovalHistory),
    [conceptApproval, conceptApprovalHistory],
  );

  const totalVersions = sortedModel.length + sortedConcept.length;
  const hasHistory = totalVersions > 0;

  const close = useCallback(() => {
    setOpen(false);
    setCollapsedBundleKeys(new Set());
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => firstOpenLinkRef.current?.focus());
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (!hasHistory) return null;

  const resolveBundleReleases = (
    bundle: PortalReleaseBundle,
    releaseById: Map<string, PortalFileRelease>,
  ): PortalFileRelease[] =>
    bundle.releaseIds.map(id => releaseById.get(id)).filter((r): r is PortalFileRelease => !!r);

  const renderReleaseActions = (
    release: PortalFileRelease,
    bundle: PortalReleaseBundle,
    assignFirstRef: boolean,
  ) => {
    const walkthroughUrl = loomUrlForRelease(release);
    return (
      <span className="pmsv-row-actions">
        <a
          ref={assignFirstRef ? firstOpenLinkRef : undefined}
          className="pmsv-open"
          href={portalHostedAssetHref(release.releasedUrl, { downloadName: release.name })}
          target="_blank"
          rel="noopener noreferrer"
          onClick={event => {
            if (!onOpenRelease) return;
            event.preventDefault();
            onOpenRelease(release, bundle);
          }}
          aria-label={`Open ${release.name}`}
        >
          Open <ExternalLink size={12} aria-hidden />
        </a>
        {walkthroughUrl ? (
          <a
            className="pmsv-open pmsv-open--walkthrough"
            href={walkthroughUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onOpenWalkthrough?.(release, bundle)}
            aria-label={`Watch Loom walkthrough for ${release.name}`}
          >
            <Video size={12} aria-hidden /> Watch
          </a>
        ) : null}
      </span>
    );
  };

  const renderApprovalBadge = (bundle: PortalReleaseBundle, approvals: PortalDeliverableApproval[]) => {
    const approval = findApprovalForBundle(approvals, bundle);
    const label = formatApprovalBadge(approval);
    if (!approval || !label) return null;
    return (
      <span className="pmsv-approved" title={label}>
        Approved
      </span>
    );
  };

  const renderStreamSection = (
    heading: string,
    icon: React.ReactNode,
    bundles: PortalReleaseBundle[],
    releaseById: Map<string, PortalFileRelease>,
    streamKey: string,
    approvals: PortalDeliverableApproval[],
  ) => {
    if (bundles.length === 0) {
      return (
        <div className="pmsv-stream-empty" key={streamKey}>
          <div className="pmsv-stream-heading">
            {icon}
            <span>{heading}</span>
          </div>
          <p className="pmsv-stream-empty-copy">No versions released yet.</p>
        </div>
      );
    }

    let firstLinkAssigned = false;

    return (
      <section className="pmsv-stream" key={streamKey} aria-label={`${heading} versions`}>
        <div className="pmsv-stream-heading">
          {icon}
          <span>{heading}</span>
          <span className="pmsv-stream-count">{bundles.length}</span>
        </div>
        <ul className="pmsv-list">
          {bundles.map((bundle, i) => {
            const isFirst = i === 0;
            const bundleReleases = resolveBundleReleases(bundle, releaseById);
            const primary =
              bundleReleases.find(r => r.id === bundle.primaryReleaseId) || bundleReleases[0];
            const isMulti = bundleReleases.length > 1;
            const bundleKey = `${streamKey}-${bundle.id || bundle.releaseVersionIndex}`;
            const expanded = isMulti && !collapsedBundleKeys.has(bundleKey);

            if (!primary) return null;

            if (!isMulti) {
              const assignFirstRef = !firstLinkAssigned;
              if (assignFirstRef) firstLinkAssigned = true;
              return (
                <li key={bundleKey} className="pmsv-row">
                  <span className="pmsv-row-main">
                    <span className="pmsv-name" title={primary.name}>
                      {primary.displayLabel || primary.name}
                    </span>
                    <span className="pmsv-meta">
                      <span className="pmsv-vchip">v{bundle.releaseVersionIndex}</span>
                      <span aria-hidden> · </span>
                      <span>{formatPortalReleaseAddedAt(bundle.releasedAt) ?? '—'}</span>
                      {isFirst ? <span className="pmsv-current">Current</span> : null}
                      {renderApprovalBadge(bundle, approvals)}
                    </span>
                  </span>
                  {renderReleaseActions(primary, bundle, assignFirstRef)}
                </li>
              );
            }

            const assignFirstRef = !firstLinkAssigned;
            if (assignFirstRef) firstLinkAssigned = true;

            return (
              <li key={bundleKey} className="pmsv-row pmsv-row--bundle">
                <div className="pmsv-bundle-head">
                  <button
                    type="button"
                    className="pmsv-bundle-toggle"
                    onClick={() =>
                      setCollapsedBundleKeys(prev => {
                        const next = new Set(prev);
                        if (next.has(bundleKey)) next.delete(bundleKey);
                        else next.add(bundleKey);
                        return next;
                      })
                    }
                    aria-expanded={expanded}
                  >
                    {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    <span className="pmsv-name">
                      {bundle.displayLabel} — v{bundle.releaseVersionIndex}
                    </span>
                    <span className="pmsv-bundle-count">{bundleReleases.length} files</span>
                        {isFirst ? <span className="pmsv-current">Current</span> : null}
                        {renderApprovalBadge(bundle, approvals)}
                      </button>
                  {renderReleaseActions(primary, bundle, assignFirstRef)}
                </div>
                {expanded && (
                  <ul className="pmsv-bundle-files">
                    {bundleReleases.map(r => (
                      <li key={r.id} className="pmsv-bundle-file">
                        <span className="pmsv-name" title={r.name}>
                          {r.displayLabel || r.name}
                        </span>
                        <a
                          className="pmsv-open"
                          href={portalHostedAssetHref(r.releasedUrl, { downloadName: r.name })}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => onOpenRelease?.(r, bundle)}
                          aria-label={`Open ${r.name}`}
                        >
                          Open <ExternalLink size={12} aria-hidden />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    );
  };

  const triggerLabel =
    totalVersions === 1 ? '1 version' : `${totalVersions} versions`;
  const isHeader = variant === 'header';

  const dialog = open
    ? createPortal(
        <div
          className="pmsv-overlay"
          role="presentation"
          onMouseDown={e => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            ref={popoverRef}
            className="pmsv-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Design deliverable version history"
          >
            <header className="pmsv-header">
              <span className="pmsv-heading">
                <History size={14} aria-hidden /> Version history
              </span>
              <button type="button" className="pmsv-close" onClick={close} aria-label="Close">
                <X size={16} aria-hidden />
              </button>
            </header>
            <div className="pmsv-body">
              {renderStreamSection(
                '3D Model',
                <Box size={14} aria-hidden />,
                sortedModel,
                modelReleaseById,
                'model',
                modelApprovals,
              )}
              {renderStreamSection(
                '2D Concept Elevations',
                <Layers2 size={14} aria-hidden />,
                sortedConcept,
                conceptReleaseById,
                'concept',
                conceptApprovals,
              )}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <span className="pmsv-wrap">
      <button
        ref={triggerRef}
        type="button"
        className={`pmsv-trigger${isHeader ? ' pmsv-trigger--header' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Version history — ${triggerLabel}`}
        title="View version history for 3D model and 2D concept elevations"
        onClick={() => setOpen(o => !o)}
      >
        {isHeader ? (
          <>
            <span className="pmsv-trigger-leading" aria-hidden>
              <span className="pmsv-trigger-icon">
                <History size={14} />
              </span>
              <span className="pmsv-trigger-reveal">
                <span className="pmsv-trigger-text">Version history</span>
                <span className="pmsv-trigger-count">{totalVersions}</span>
              </span>
            </span>
            <ChevronDown size={13} className="pmsv-trigger-chevron" />
          </>
        ) : (
          <>
            <History size={13} aria-hidden />
            <span className="pmsv-trigger-text">{triggerLabel}</span>
          </>
        )}
      </button>
      {dialog}
    </span>
  );
};

export default PortalModelStepVersionsPopover;
